import { Alert, Box } from "@mui/material";

import {
  AISummaryMetadataBar,
  AISummaryMarkdownLink,
  EntityLink,
} from "@/shared/components/ai";
import { PhraseProgressBar } from "@/shared/components/PhraseProgressBar";
import { RobotoMarkdown } from "@/shared/components/RobotoMarkdown";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { AISummaryStatus } from "@/shared/domain/ai_summary";
import { useDatasetSummary } from "@/shared/domain/datasets/hooks/useDatasetSummary";
import { useRegenerateDatasetSummary } from "@/shared/domain/datasets/hooks/useRegenerateDatasetSummary";
import { Button } from "@/shared/foundation/primitives";

import { useDatasetContext } from "../useDatasetContext";

import styles from "./DatasetAISummary.module.css";

export function DatasetAISummary() {
  const { datasetId } = useDatasetContext();
  const { data, isLoading, isError } = useDatasetSummary(datasetId);
  const regenerateMutation = useRegenerateDatasetSummary();

  const showLoadingWidget =
    isLoading ||
    regenerateMutation.isPending ||
    (data?.status === AISummaryStatus.Pending && data?.text === "");

  // A 404 from the summary endpoint maps to `null`: no summary has been
  // generated yet. Offer an explicit "Generate" action rather than auto-
  // kicking-off generation (and spending credits) just because the tab opened.
  //
  // A terminal (Complete) summary with empty text is just as unreadable as no
  // summary at all — reachable e.g. via an empty set-summary — and would
  // otherwise render a blank body under the metadata bar. Fold it into the same
  // empty state so the user always has a call to action. (Pending-empty is the
  // loading case above; Failed has its own alert below.)
  const isEmptyTerminalSummary =
    !!data && data.status === AISummaryStatus.Complete && data.text === "";
  const showGenerate =
    !showLoadingWidget && !isError && (data === null || isEmptyTerminalSummary);

  const showSummary =
    !showLoadingWidget &&
    !!data &&
    data.status !== AISummaryStatus.Failed &&
    data.text !== "";

  return (
    <Box className={styles.container}>
      {data?.created && !showGenerate && (
        <AISummaryMetadataBar
          created={data.created}
          isRegenerating={
            regenerateMutation.isPending ||
            data?.status === AISummaryStatus.Pending
          }
          onRegenerateClick={() => regenerateMutation.mutate({ datasetId })}
          context={{ datasetId, created: data.created }}
        />
      )}

      {showLoadingWidget && (
        <Box className={styles.loadingContainer}>
          <PhraseProgressBar />
        </Box>
      )}

      {showGenerate && (
        <Box className={styles.emptyState}>
          <RobotoTypography variant="body2">
            No AI summary is available for this dataset yet.
          </RobotoTypography>
          <Button
            variant="contained"
            // `useRegenerateDatasetSummary` is a plain POST to the summary
            // endpoint that generates whether or not one already exists, so it
            // backs first-time generation here as well as regeneration above —
            // "regenerate" is just the hook's name, not the action at this site.
            onClick={() => regenerateMutation.mutate({ datasetId })}
          >
            Generate summary
          </Button>
        </Box>
      )}

      {!showLoadingWidget && isError && (
        <Alert severity="error">
          Failed to load summary. Please try again later.
        </Alert>
      )}

      {regenerateMutation.error && (
        <Alert severity="error">
          Failed to regenerate summary. Please try again later.
        </Alert>
      )}

      {data?.status === AISummaryStatus.Failed && (
        <Alert severity="error">
          Failed to generate summary. Please try regenerating.
        </Alert>
      )}

      {showSummary && (
        <RobotoMarkdown
          content={data?.text || ""}
          datasetLinkRenderer={({ datasetId: linkedDatasetId, text }) => {
            // Self-references render as plain text: a link back to the
            // dataset the user is already viewing would be confusing.
            if (linkedDatasetId === datasetId) {
              return <span>{text}</span>;
            } else {
              return (
                <AISummaryMarkdownLink datasetId={datasetId} text={text} />
              );
            }
          }}
          fileLinkRenderer={({ fileId, text, time }) => (
            <AISummaryMarkdownLink fileId={fileId} text={text} time={time} />
          )}
          topicLinkRenderer={({ topicId, text, time }) => (
            <AISummaryMarkdownLink topicId={topicId} text={text} time={time} />
          )}
          messagePathLinkRenderer={({ messagePathId, text, time }) => (
            <AISummaryMarkdownLink
              messagePathId={messagePathId}
              text={text}
              time={time}
            />
          )}
          entityLinkRenderer={(props) => <EntityLink {...props} />}
        />
      )}
    </Box>
  );
}
